import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { HelpCircle, X, ChevronLeft, ChevronRight, MessageCircle, BarChart3, Eye, Brain, Mic, Edit3, Rocket, Upload, FileText, Sparkles, Target, Menu } from 'lucide-react';

// Separate keys for each flow
const TOUR_KEYS = {
  create: 'edoTourCreate',
  improve: 'edoTourImprove'
};

export default function OnboardingTour({ entryType = 'create', forceStart = false, onComplete = null, onOpenMobileMenu = null }) {
  const { language } = useLanguage();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const tourKey = TOUR_KEYS[entryType] || TOUR_KEYS.create;

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Different steps for create vs improve
  const createStepsFr = [
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Discutez, c'est tout !",
      content: "Racontez votre parcours naturellement. L'IA construit votre CV automatiquement.",
      color: "from-[#5BA946] to-emerald-600"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Progression en temps réel",
      content: isMobile 
        ? "Appuyez sur ☰ pour voir votre CV se compléter section par section."
        : "La barre latérale montre votre CV se remplir en direct.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "Téléchargez et postulez !",
      content: "Exportez votre CV en PDF professionnel. Vous pouvez aussi utiliser le micro pour dicter vos réponses. 🎤",
      color: "from-amber-500 to-yellow-500"
    }
  ];

  const createStepsEn = [
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Just chat!",
      content: "Tell your story naturally. The AI builds your CV automatically.",
      color: "from-[#5BA946] to-emerald-600"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Real-time progress",
      content: isMobile 
        ? "Tap ☰ to watch your CV fill up section by section."
        : "The sidebar shows your CV filling up in real-time.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "Download & apply!",
      content: "Export your CV as a professional PDF. You can also use the microphone to dictate your answers. 🎤",
      color: "from-amber-500 to-yellow-500"
    }
  ];

  const improveStepsFr = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Améliorez votre CV !",
      content: "Notre IA analyse votre CV et vous donne des conseils personnalisés pour l'améliorer.",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: <Upload className="w-8 h-8" />,
      title: "Importez votre CV",
      content: "Formats acceptés : PDF, DOCX, TXT. Taille max : 20 MB.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Rapport de feedback",
      content: "Recevez un rapport détaillé avec des recommandations pour chaque section.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Discutez pour améliorer",
      content: "Posez des questions à l'IA pour approfondir les conseils.",
      color: "from-cyan-500 to-cyan-600"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Test Soft Power",
      content: isMobile 
        ? "Appuyez ci-dessous pour voir où se trouve le test !"
        : "Enrichissez votre profil avec votre personnalité professionnelle.",
      color: "from-pink-500 to-rose-600",
      showMenuButton: isMobile
    },
    {
      icon: <Edit3 className="w-8 h-8" />,
      title: "Commençons !",
      content: "Importez votre CV pour démarrer l'analyse.",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Décrochez votre job !",
      content: "Utilisez votre CV amélioré dans EdoMatch ou téléchargez-le pour vos candidatures !",
      color: "from-amber-500 to-yellow-500"
    }
  ];

  const improveStepsEn = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Improve your CV!",
      content: "Our AI analyzes your CV and gives you personalized tips to improve it.",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: <Upload className="w-8 h-8" />,
      title: "Import your CV",
      content: "Accepted formats: PDF, DOCX, TXT. Max size: 20 MB.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Feedback report",
      content: "Receive a detailed report with recommendations for each section.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Chat to improve",
      content: "Ask questions to the AI to get deeper insights.",
      color: "from-cyan-500 to-cyan-600"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Soft Power Test",
      content: isMobile 
        ? "Tap below to see where the test is located!"
        : "Enrich your profile with your professional personality.",
      color: "from-pink-500 to-rose-600",
      showMenuButton: isMobile
    },
    {
      icon: <Edit3 className="w-8 h-8" />,
      title: "Let's start!",
      content: "Import your CV to begin analysis.",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Land your dream job!",
      content: "Use your improved CV in EdoMatch or download it for your applications!",
      color: "from-amber-500 to-yellow-500"
    }
  ];

  // Select steps based on entryType and language
  const getSteps = () => {
    if (entryType === 'improve') {
      return language === 'fr' ? improveStepsFr : improveStepsEn;
    }
    return language === 'fr' ? createStepsFr : createStepsEn;
  };

  const steps = getSteps();
  const totalSteps = steps.length;
  const currentStepData = steps[currentStep];

  // Start tour on mount if not completed or if forceStart
  useEffect(() => {
    if (forceStart) {
      setIsActive(true);
      return;
    }
    
    const tourCompleted = localStorage.getItem(tourKey);
    if (!tourCompleted) {
      const timer = setTimeout(() => setIsActive(true), 800);
      return () => clearTimeout(timer);
    }
  }, [tourKey, forceStart]);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTour();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const completeTour = () => {
    setIsActive(false);
    localStorage.setItem(tourKey, 'true');
    if (onComplete) {
      onComplete();
    }
  };

  const restartTour = () => {
    setCurrentStep(0);
    setIsActive(true);
  };

  // Help button when tour is not active
  if (!isActive) {
    return (
      <button
        onClick={restartTour}
        className="fixed bottom-6 right-4 z-50 w-12 h-12 rounded-full bg-white shadow-lg border-2 border-gray-100 flex items-center justify-center text-gray-600 hover:text-[#5BA946] hover:border-[#5BA946] hover:scale-110 transition-all duration-200 active:scale-95"
        style={{ WebkitTapHighlightColor: 'transparent' }}
        aria-label={language === 'fr' ? 'Aide' : 'Help'}
      >
        <HelpCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <>
      {/* Dark overlay */}
      <div 
        className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm"
        onClick={completeTour}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      />
      
      {/* Tour Card - Centered */}
      <div 
        className="fixed z-[10002] w-[calc(100%-32px)] max-w-sm"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden animate-scaleIn">
          {/* Gradient Header */}
          <div className={`bg-gradient-to-br ${currentStepData.color} p-5 pb-6 text-center relative`}>
            {/* Close button */}
            <button
              onClick={completeTour}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Icon */}
            <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
              {currentStepData.icon}
            </div>
            
            {/* Title */}
            <h2 className="text-lg font-bold text-white">
              {currentStepData.title}
            </h2>
          </div>
          
          {/* Content */}
          <div className="p-5 pt-4 text-center">
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              {currentStepData.content}
            </p>
            
            {/* Show Menu Button for Soft Power step on mobile */}
            {currentStepData.showMenuButton && (
              <button
                onClick={() => {
                  if (onOpenMobileMenu) {
                    onOpenMobileMenu();
                  }
                }}
                className={`w-full mb-4 py-3 px-4 rounded-xl bg-gradient-to-r ${currentStepData.color} text-white font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition-all text-sm shadow-md`}
              >
                <Menu className="w-5 h-5" />
                {language === 'fr' ? 'Ouvrir le menu ☰' : 'Open menu ☰'}
              </button>
            )}
            
            {/* Progress indicator - Very subtle line with dot */}
            <div className="flex items-center justify-center gap-0.5 mb-4">
              {steps.map((_, index) => (
                <div
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`cursor-pointer transition-all duration-300 rounded-full ${
                    index === currentStep 
                      ? 'w-5 h-1 bg-[#5BA946]' 
                      : 'w-1 h-1 bg-gray-300'
                  }`}
                  style={{ minWidth: index === currentStep ? '20px' : '4px' }}
                />
              ))}
            </div>
            
            {/* Navigation buttons */}
            <div className="flex gap-3">
              {currentStep > 0 ? (
                <button
                  onClick={handleBack}
                  className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-600 font-medium flex items-center justify-center gap-1.5 hover:bg-gray-50 active:scale-[0.98] transition-all text-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {language === 'fr' ? 'Retour' : 'Back'}
                </button>
              ) : (
                <button
                  onClick={completeTour}
                  className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-400 font-medium hover:bg-gray-50 active:scale-[0.98] transition-all text-sm"
                >
                  {language === 'fr' ? 'Passer' : 'Skip'}
                </button>
              )}
              
              <button
                onClick={handleNext}
                className={`flex-1 py-3 px-4 rounded-xl bg-gradient-to-r ${currentStepData.color} text-white font-medium flex items-center justify-center gap-1.5 hover:opacity-90 active:scale-[0.98] transition-all shadow-lg text-sm`}
              >
                {currentStep === totalSteps - 1 
                  ? (language === 'fr' ? 'Commencer' : 'Start') 
                  : (language === 'fr' ? 'Suivant' : 'Next')
                }
                {currentStep < totalSteps - 1 && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Step counter - More subtle */}
            <p className="text-[11px] text-gray-300 mt-3 font-medium">
              {currentStep + 1} / {totalSteps}
            </p>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </>
  );
}
